/**
 * Sdílená logika phone submenu (carusel chipů + swipe rail).
 * Kanón: docs/2.1/phone-ui-standard.md §4 / PhoneCodable.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type RefObject,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

/** Codable projekt v pracovním režimu (náhled+chat) — schovat shell chrome. */
export function isPhoneCodableWorkFocus(pathname: string, search = ''): boolean {
  if (!pathname.startsWith('/phone/codable/')) return false;
  const id = pathname.split('/')[3];
  if (!id) return false;
  const tab = new URLSearchParams(search).get('tab');
  return tab === 'work' || tab === 'preview' || tab === 'chat';
}

export interface PhoneSectionDef<T extends string = string> {
  id: T;
  /** Hotový i18n label (už přeložený). */
  label: string;
  icon: LucideIcon;
}

export interface UsePhoneSectionsResult<T extends string> {
  section: T;
  sectionIndex: number;
  goSection: (idOrIndex: T | number) => void;
  railRef: RefObject<HTMLDivElement | null>;
  chipRefs: MutableRefObject<Partial<Record<T, HTMLButtonElement | null>>>;
  onRailScroll: () => void;
}

export function usePhoneSections<T extends string>(
  sections: PhoneSectionDef<T>[],
  opts?: {
    /** Default section id. */
    defaultId?: T;
    /** Query param (default `tab`). */
    param?: string;
  },
): UsePhoneSectionsResult<T> {
  const param = opts?.param ?? 'tab';
  const defaultId = opts?.defaultId ?? sections[0]!.id;
  const ids = new Set(sections.map((s) => s.id));

  const normalize = useCallback(
    (raw: string | null): T => {
      if (raw && ids.has(raw as T)) return raw as T;
      return defaultId;
    },
    // sections identity — recompute when ids list changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultId, sections.map((s) => s.id).join('|')],
  );

  const [search, setSearch] = useSearchParams();
  const fromUrl = normalize(search.get(param));
  const [section, setSection] = useState<T>(fromUrl);

  const railRef = useRef<HTMLDivElement | null>(null);
  const chipRefs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});
  const scrollLock = useRef(false);
  const skipNextSync = useRef(false);
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const urlDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (fromUrl !== section) setSection(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl]);

  const sectionIndex = Math.max(
    0,
    sections.findIndex((s) => s.id === section),
  );

  const scrollToIndex = useCallback((index: number, smooth: boolean) => {
    const rail = railRef.current;
    if (!rail) return;
    const w = rail.clientWidth || 1;
    const target = index * w;
    if (Math.abs(rail.scrollLeft - target) < 2) return;
    scrollLock.current = true;
    rail.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });
    if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = setTimeout(() => {
      scrollLock.current = false;
      if (railRef.current) {
        const ww = railRef.current.clientWidth || 1;
        railRef.current.scrollTo({ left: index * ww, behavior: 'auto' });
      }
    }, smooth ? 480 : 40);
  }, []);

  const writeUrl = useCallback(
    (nextId: T) => {
      setSearch((prev) => {
        const n = new URLSearchParams(prev);
        if (nextId === defaultId) n.delete(param);
        else n.set(param, nextId);
        return n;
      }, { replace: true });
    },
    [defaultId, param, setSearch],
  );

  const goSection = useCallback(
    (idOrIndex: T | number) => {
      const nextId = typeof idOrIndex === 'number'
        ? sections[Math.max(0, Math.min(sections.length - 1, idOrIndex))]?.id
        : idOrIndex;
      if (!nextId) return;
      const idx = sections.findIndex((s) => s.id === nextId);
      if (nextId === section && idx >= 0) {
        scrollToIndex(idx, true);
        return;
      }
      skipNextSync.current = true;
      setSection(nextId);
      writeUrl(nextId);
      if (idx >= 0) scrollToIndex(idx, true);
    },
    [section, sections, scrollToIndex, writeUrl],
  );

  useEffect(() => {
    if (skipNextSync.current) {
      skipNextSync.current = false;
    } else {
      scrollToIndex(sectionIndex, false);
    }
    chipRefs.current[section]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [section, sectionIndex, scrollToIndex]);

  const onRailScroll = () => {
    if (scrollLock.current) return;
    const rail = railRef.current;
    if (!rail) return;
    const w = rail.clientWidth || 1;
    const i = Math.round(rail.scrollLeft / w);
    const next = sections[i];
    if (!next || next.id === section) return;
    setSection(next.id);
    if (urlDebounce.current) clearTimeout(urlDebounce.current);
    urlDebounce.current = setTimeout(() => writeUrl(next.id), 150);
  };

  return {
    section,
    sectionIndex,
    goSection,
    railRef,
    chipRefs,
    onRailScroll,
  };
}
